import {
  Box,
  Button,
  Container,
  createStyles,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import SaveIcon from '@material-ui/icons/Save';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import FilterSavingModal from '../../../../components/Modals/filterSavingModal';
import { useAuth } from '../../../../contexts/AuthContext/AuthContext';
import { api } from '../../../../core/api';
import { CONSTANTS } from '../../../../core/config/constants';
import { Width } from '../../../../core/config/mainTheme';
import { TabNamesEnum } from '../../../Benchmark/BenchmarkPage/BenchmarkTabs/BenchmarkTabs';
import {
  useBenchmarkDispatch,
  useBenchmarkState,
} from '../../../Benchmark/Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../../../Benchmark/Context/BenchmarkContext.types';
import { useErrorsDispatch } from '../../shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../shared/ErrorContext/ErrorContext.types';
import {
  useFilterDispatch,
  useFilterState,
} from '../../shared/FinancialDataContext/FinancialDataContext';
import { FinancialDataContextActionTypes } from '../../shared/FinancialDataContext/FinancialDataContext.types';
import { ActivityKeywordsDialog } from '../ActivityKeywordsChart/ActivityKeywordsDialog';
import { MinimizedActivityKeywordsChart } from '../ActivityKeywordsChart/MinimizedActivityKeywordsChart';
import AppliedFilters from './AppliedFilters';
import { CompaniesFilterAssist } from './CompaniesFilterAssist';
import { CompaniesFilterAssistProvider } from './CompaniesFilterAssistContext';
import FilterHeader from './FilterHeader';
import FilterLibrary from './FilterLibrary';
import FilterSubHeader from './FilterSubHeader';

interface Keywords {
  name: string;
  value: number;
}

interface OwnProps {
  benchmarkRefinement: boolean;
}

const CompaniesFilter: React.FC<OwnProps> = React.memo(({ benchmarkRefinement }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [keywordsData, setKeywordsData] = useState<Keywords[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [cancelRefinementLoading, setCancelRefinementLoading] = useState(false);
  const [assistVisible, setAssistVisible] = useState(true);
  const { user } = useAuth();

  const { data, historySearch, requestFiltersData } = useFilterState();
  const { activeBenchmark } = useBenchmarkState();

  const dispatch = useFilterDispatch();
  const errorDispatch = useErrorsDispatch();
  const benchmarkDispatch = useBenchmarkDispatch();

  const isButtonDisabled = !Object.keys(data).length;

  const handleClearFilter = () => {
    dispatch({
      type: FinancialDataContextActionTypes.ClearAppliedFilters,
      payload: [],
    });
    setKeywordsData([]);
  };

  useEffect(() => {
    const keywordsLegnth = Object.keys(requestFiltersData)?.length;
    const getActiveKeywords = async () => {
      const filters = {
        filters: requestFiltersData,
        historySearch,
      };
      const data = await api.companySearch.getActivityKeywords(filters);
      const mappedKeywords = mapKeywordsReponse(data.data.nodes);

      setKeywordsData(mappedKeywords);
    };
    if (keywordsLegnth > 0) {
      getActiveKeywords();
    } else {
      setKeywordsData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestFiltersData]);

  const handleCancelRefinementClick = async () => {
    setCancelRefinementLoading(true);
    try {
      if (activeBenchmark?.id && activeBenchmark?.refinementSearchId) {
        await api.benchmark.updateRefinementSearchId(activeBenchmark.id, {
          refinementSearchId: undefined,
        });
        const response = await api.benchmark.fetchBenchmarkDetails(activeBenchmark.id);

        if (response) {
          benchmarkDispatch({
            type: BenchmarkContextActionTypes.SetActiveBenchmarkCompanyId,
            payload: '',
          });
          benchmarkDispatch({
            type: BenchmarkContextActionTypes.SetClickedBenchmarkTabName,
            payload: TabNamesEnum.MANUAL,
          });
          benchmarkDispatch({
            type: BenchmarkContextActionTypes.SetActiveBenchmark,
            payload: response,
          });
        }
      }
    } catch (error) {
      errorDispatch({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
    } finally {
      setCancelRefinementLoading(false);
    }
  };
  const disableAssist = !!(window as any)?.env?.DISABLE_ASSIST && !user?.showPreviewFeatures;

  // Dynamic height calculation based on assistant visibility
  const filtersBoxHeight = React.useMemo(() => {
    const baseOffset = 360;
    const assistantOffset = !disableAssist && assistVisible ? 60 : 0; // Approximate assistant height offset
    return `calc(100vh - ${baseOffset + assistantOffset}px)`;
  }, [disableAssist, assistVisible]);

  return (
    <Container className={classes.root}>
      <FilterHeader
        benchmarkRefinement={benchmarkRefinement}
        cancelRefinementLoading={cancelRefinementLoading}
        setShowConfirm={setShowConfirm}
        assistVisible={assistVisible}
        setAssistVisible={setAssistVisible}
        disableAssist={disableAssist}
      />
      <CompaniesFilterAssistProvider>
        {/* Keep the elegant toggle for assist below header for now */}
        {/* You can remove this block after confirming the header toggle */}
        {/* 
        {!disableAssist && (
          <Box className={classes.assistToggleRow}>
            <Button
              className={classes.assistToggleButton}
              onClick={() => setAssistVisible((v) => !v)}
              startIcon={assistVisible ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            >
              {assistVisible ? 'Hide Assist' : 'Show Assist'}
            </Button>
          </Box>
        )}
        */}
        {!disableAssist && assistVisible && (
          <Box className={classes.assistBox}>
            <CompaniesFilterAssist />
          </Box>
        )}
      </CompaniesFilterAssistProvider>
      <Box className={classes.conainter}>
        <Box className={classes.grid}>
          <Grid
            item
            className={`${classes.gridItem} ${classes.filterSelection} ${classes.filterHolder}`}
          >
            <FilterLibrary />
          </Grid>

          <Grid
            item
            className={`${classes.gridItem} ${classes.filterHolder} ${classes.appliedFilters}`}
            data-cy="applied-filters"
          >
            <Box>
              <Box className={classes.headerBox}>
                <FilterSubHeader text={t('companies:appliedFilters')} />
                <Button
                  disabled={isButtonDisabled}
                  className={classes.clearButton}
                  onClick={() => {
                    handleClearFilter();
                  }}
                >
                  <ClearIcon />
                  <Typography>{t('app:clearFilters')}</Typography>
                </Button>
                <Button
                  disabled={isButtonDisabled}
                  className={classes.filterButton}
                  onClick={() => {
                    setShowFilterModal(!showFilterModal);
                  }}
                >
                  <SaveIcon />
                  <Typography>{t('app:save')}</Typography>
                </Button>
              </Box>
              {/* Remove CompaniesFilterAssist from here */}
              <Box className={classes.filtersBox} style={{ height: filtersBoxHeight }}>
                <AppliedFilters />
              </Box>
              {!!keywordsData.length && (
                <Box className={classes.activityKeywordsBox}>
                  <Box>
                    <MinimizedActivityKeywordsChart
                      keywordsData={keywordsData}
                      id={1}
                      onClick={() => setIsOpen(true)}
                    />
                    <ActivityKeywordsDialog
                      keywordsData={keywordsData}
                      id={2}
                      isOpen={isOpen}
                      handleClose={() => setIsOpen(false)}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
        </Box>
      </Box>

      <FilterSavingModal
        show={showFilterModal}
        handleClose={() => setShowFilterModal(!showFilterModal)}
      />
      <ConfirmationDialog
        open={showConfirm}
        handleClose={() => {
          setShowConfirm(false);
        }}
        handleConfirmOk={async () => {
          setShowConfirm(false);
          await handleCancelRefinementClick();
        }}
        text="app:confirmationText:companies:cancelRefinementConfirm"
      />
    </Container>
  );
});

function mapKeywordsReponse(keywords: { key: string; count: number }[]): Keywords[] {
  return keywords.map((k) => ({ name: k.key, value: k.count }));
}

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: 0,
      marginRight: theme.spacing(8),
      width: Width.filterBlockWdith,
      maxWidth: Width.filterBlockWdith,
    },
    conainter: {
      height: '100%',
    },
    grid: {
      height: '100%',
      paddingLeft: theme.spacing(4),
      display: 'flex',
      justifyContent: 'space-between',
      width: Width.filterBlockWdith,
      maxWidth: Width.filterBlockWdith,
    },
    gridItem: {
      padding: 0,
      width: '49%',
      borderLeft: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
      background: '#fff',
    },
    appliedFilters: {
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'visible',
      overflowX: 'visible',
    },
    filtersBox: {
      overflowY: 'scroll',
      overflowX: 'hidden',
      // height: 'calc(100vh - 360px)', // Now handled dynamically via filtersBoxHeight
    },
    filterHolder: {
      maxHeight: 'calc(100vh - 150px)',
      zIndex: 1,
    },
    filterSelection: {
      overflowY: 'auto',
      overflowX: 'hidden',
      maxHeight: 'calc(100vh - 150px)',
      backgroundColor: theme.palette.primary.contrastText,
    },
    headerBox: {
      display: 'flex',
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderLeft: `1px solid ${theme.palette.divider}`,
      height: CONSTANTS.TABLE_HEADER,
    },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      marginBottom: theme.spacing(1),
    },
    assistToggleButtonHeader: {
      textTransform: 'none',
      fontWeight: 400,
      fontSize: 13,
      padding: theme.spacing(0.5, 1),
      borderRadius: 16,
      background: 'transparent',
      color: theme.palette.text.secondary,
      marginLeft: theme.spacing(2),
      minWidth: 0,
      boxShadow: 'none',
      height: 32,
      alignSelf: 'center',
      '& .MuiButton-startIcon': {
        marginRight: 4,
      },
      '&:hover': {
        background: theme.palette.action.hover,
      },
    },
    filterButton: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: 'auto',
      borderRadius: 0,
      borderLeft: `1px solid ${theme.palette.divider}`,
      lineHeight: '1.75',
      background: theme.palette.secondary.main,
      '& svg': {
        fontSize: '18px',
        marginRight: theme.spacing(2),
      },
      '& .MuiTypography-root': {
        fontSize: theme.typography.caption.fontSize,
        textTransform: 'uppercase',
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.dark,
      },
    },
    clearButton: {
      minWidth: '130px',
      background: theme.palette.secondary.main,
      borderRadius: 0,
      borderLeft: `1px solid ${theme.palette.divider}`,
      '& svg': {
        fontSize: '21px',
        marginRight: theme.spacing(2),
      },
      '& .MuiTypography-root': {
        fontSize: theme.typography.caption.fontSize,
        lineHeight: '1.2',
        textTransform: 'uppercase',
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.dark,
      },
    },
    activityKeywordsBox: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      justifyContent: 'flex-end',

      bottom: 1,
      width: '100%',
    },
    assistBox: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    assistToggleRow: {
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginBottom: theme.spacing(1),
    },
    assistToggleButton: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: 14,
      padding: theme.spacing(0.5, 2),
      borderRadius: 20,
      background: theme.palette.grey[100],
      color: theme.palette.text.primary,
      boxShadow: 'none',
      '&:hover': {
        background: theme.palette.grey[300],
      },
    },
  }),
);

export default CompaniesFilter;
